<template>
  <div>
    <v-card outlined class="mb-2 pa-3">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-col cols="12">
          <v-row>
            <v-col cols="8" v-if="!editMode">
              <h3>{{ titrationScheme.name }}</h3>
            </v-col>
            <v-col v-if="editMode">
              <v-text-field
                v-model="tempData.name"
                outlined
                :label="$ucfirst($t('myWeekMedicationDetail.name', { catalog: 'app_mobile' }))"
                required
                dense
                :rules="nameRules"
              ></v-text-field>
            </v-col>
            <v-col cols="4" align="right" v-if="!readonly">
              <v-btn
                class="mr-3"
                fab
                text
                color="primary"
                @click="editScheme()"
                v-if="!editMode"
              >
                <eva-icon name="edit-2-outline"></eva-icon>
              </v-btn>
              <v-btn
                class="mr-3"
                fab
                text
                color="primary"
                @click="saveTitrationScheme()"
                :disabled="!valid"
                v-if="editMode"
              >
                <eva-icon name="save-outline"></eva-icon>
              </v-btn>
              <v-btn
                fab
                text
                color="error"
                @click="deleteScheme()"
              >
                <eva-icon fill="red" name="trash-2-outline"></eva-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="!editMode">
            <v-col cols="2">{{ $ucfirst($t("dss.startMedication")) }}</v-col>
            <v-col>
              <ul>
                <li v-for="c in titrationScheme.startClasses" v-bind:key="c.id">
                  {{ c.name }}
                </li>
              </ul>
            </v-col>
          </v-row>
          <v-row v-if="editMode">
            <v-col cols="2">
              <p class="pt-2 mb-0">
                {{ $ucfirst($t("dss.startMedication")) }}
              </p>
            </v-col>
            <v-col>
              <v-select
                :items="medicationClassesToChooseFrom"
                outlined
                multiple
                :label="$ucfirst($t('dss.startMedicationClasses'))"
                required
                dense
                v-model="tempData.startMedicationClasses"
                item-text="name"
                :rules="medicationClassRules"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="!editMode">
            <v-col cols="2">{{ $ucfirst($t("dss.additionalMedication")) }}</v-col>
            <v-col>
              <ul>
                <li
                  v-for="c in titrationScheme.additionalClasses"
                  v-bind:key="c.id"
                >
                  {{ c.name }}
                </li>
              </ul>
            </v-col>
          </v-row>
          <v-row v-if="editMode">
            <v-col cols="2">
              <p class="pt-2 mb-0">
                {{ $ucfirst($t("dss.additionalMedication")) }}
              </p>
            </v-col>
            <v-col>
              <v-select
                :items="medicationClassesToChooseFrom"
                outlined
                multiple
                :label="$ucfirst($t('dss.additionalMedicationClasses'))"
                required
                dense
                v-model="tempData.additionalMedicationClasses"
                item-text="name"
                :rules="medicationClassRules"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="!editMode">
            <v-col cols="2">{{ $ucfirst($t("dss.description")) }}</v-col>
            <v-col>{{ titrationScheme.description || $t('dss.noDescriptionAdded') }}</v-col>
          </v-row>
          <v-row v-if="editMode">
            <v-col cols="2">
              <p class="pt-2 mb-0">
                {{ $ucfirst($t("dss.description")) }}
              </p>
            </v-col>
            <v-col>
              <v-text-field
                v-model="tempData.description"
                outlined
                :label="$ucfirst($t('dss.description'))"
                required
                dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-form>
    </v-card>
    <v-dialog
      v-model="showDialog"
      width="600px"
    >
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{
            this.$ucfirst(
                this.$t("dss.areYouSureToDeleteTitrationScheme", {
                  params: {
                    titrationSchemeName:
                        this.tempData.name ||
                        this.$ucfirst(
                            this.$t("dss.noName")
                        ),
                  },
                })
            )
          }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn
              x-large
              text
              class="ml-4"
              @click="deleteScheme"
          >
            {{ $ucfirst($t('confirm')) }}
          </v-btn>
          <v-btn
              x-large
              text
              class="ml-4"
              @click="showDialog = false"
              color="primary"
          >
            {{ $ucfirst($t('buttons.cancel', {catalog: 'app_mobile'})) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
/**
 * Component DrugAdditionTitrationSchemeCard.vue
 *
 * @module DrugAdditionTitrationSchemeCard
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/components/Parts/DSS/DrugAdditionTitrationSchemeCard.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "DrugAdditionTitrationSchemeCard",
  props: {
    titrationScheme: {
      type: Object,
    },
    medicationClassesToChooseFrom: {
      type: Array,
    },
    readonly: {
      type: Boolean,
      default: false,
    }
  },
  data: function () {
    return {
      showDialog: false,
      valid: true,
      editMode: false,
      tempData: {},
      nameRules: [
        (v) => !!v || this.$ucfirst(this.$t("validation.IS_REQUIRED")),
      ],
      medicationClassRules: [
        (v) =>
          v.length > 0 ||
          this.$ucfirst(this.$t("validation.atLeastOneMedicationClass")),
      ],
    };
  },
  mounted: function () {
    this.tempData = {
      type: "DRUG_ADDITION",
      id: this.titrationScheme.id,
      name: this.titrationScheme.name,
      description: this.titrationScheme.description,
      startMedicationClasses: this.titrationScheme.startClasses,
      additionalMedicationClasses: this.titrationScheme.additionalClasses,
    };
    this.setInputWatchers();
  },
  methods: {
    medicationClassesToString(medicationClassesArray) {
      const arrayToJoin = [];

      medicationClassesArray.forEach((medicationClass) => {
        arrayToJoin.push(medicationClass.name);
      });

      return arrayToJoin.join(", ");
    },
    saveTitrationScheme() {
      if (this.$refs.form.validate()) {
        this.$emit("save-scheme", this.tempData);
        this.editMode = false;
      } else {
        console.log("THERE ARE ERRORS IN THE FORM");
      }
    },
    editScheme() {
      this.editMode = true;
    },
    deleteScheme() {
      if (!this.showDialog) {
        this.showDialog = true;
      } else {
        this.$emit("delete-scheme", this.$props.titrationScheme.id);
        this.showDialog = false;
      }
    },
    setInputWatchers() {
      const keys = Object.keys(this.tempData);
      keys.forEach((key) => {
        this.$watch(`tempData.${key}`, {
          deep: true,
          handler: function() {
            const data = {
              unsavedChanges: true,
              screen: 'titrationSchemes',
              name: `addTitrationScheme-${this.tempData.id}`
            }
            this.$store.dispatch('patientSummary/toggleMultipleUnsavedChanges', data);
          }
        })
      })
    }
  },
};
</script>

<style lang="scss">
@import "../../../../../scss/colors";
</style>